import type { CoordeRunSummary } from '@cortex/domain-ac-mens-hub-api';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { useCoordeRunList } from '../hooks/useCoordeRun.js';

/**
 * 生成履歴の一覧。
 *
 * 生成は非同期なので、依頼した本人がページを閉じても結果は残る。後から戻ってくる導線が
 * 無いと、承認されないまま忘れられた画像が溜まる（＝素材として使えないまま費用だけ残る）。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Styling, Marketing
 * @graph-business Lists past generation runs and how many results still need a decision, so nothing generated is silently forgotten before it can be used.
 * @graph-connects useCoordeRunList [calls] 履歴一覧の取得
 */

/**
 * 判定が残っているランかどうか。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling, Marketing
 * @graph-business Flags the runs that still need someone to look at them, which is the action this list exists to trigger.
 * @graph-connects none
 */
export function needsJudgement(run: CoordeRunSummary): boolean {
  return run.unjudgedCount > 0;
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Styling, Marketing
 * @graph-business Shows recent generation runs newest first with their outstanding decisions, so a requester can return to unfinished work.
 * @graph-connects useCoordeRunList [calls] 履歴一覧の取得
 */
export function CoordeHistoryPage() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useCoordeRunList();

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-[var(--color-text-muted)]">
        {t('app.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-[var(--color-error)]">
        {t('common.fetchError')}
      </div>
    );
  }

  const runs = data?.runs ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-semibold tracking-tight text-[var(--color-text-primary)]">
            {t('coordeHistory.title')}
          </h1>
          <p className="mt-1 text-[13px] text-[var(--color-text-muted)]">
            {t('coordeHistory.description')}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Link
            to="/coorde-capacity"
            className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-[13px] text-[var(--color-text-muted)]"
          >
            {t('coordeHistory.capacity')}
          </Link>
          <Link
            to="/coorde/new"
            className="rounded-md border border-[var(--color-accent-primary)] px-3 py-1.5 text-[13px] text-[var(--color-text-primary)]"
          >
            {t('coordeHistory.newRun')}
          </Link>
        </div>
      </div>

      {runs.length === 0 ? (
        <p className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-6 text-center text-[13px] text-[var(--color-text-muted)]">
          {t('coordeHistory.empty')}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-[var(--color-border)]">
          <table className="w-full min-w-[720px] border-collapse bg-[var(--color-surface)] text-[13px]">
            <caption className="sr-only">{t('coordeHistory.caption')}</caption>
            <thead>
              <tr className="border-b border-[var(--color-border)] text-left text-[12px] text-[var(--color-text-muted)]">
                <th className="px-4 py-2 font-normal">{t('coordeHistory.column.createdAt')}</th>
                <th className="px-4 py-2 font-normal">{t('coordeHistory.column.season')}</th>
                <th className="px-4 py-2 font-normal">{t('coordeHistory.column.status')}</th>
                <th className="px-4 py-2 text-right font-normal">
                  {t('coordeHistory.column.generated')}
                </th>
                <th className="px-4 py-2 text-right font-normal">
                  {t('coordeHistory.column.approved')}
                </th>
                <th className="px-4 py-2 text-right font-normal">
                  {t('coordeHistory.column.unjudged')}
                </th>
                <th className="px-4 py-2 font-normal">{t('coordeHistory.column.requestedBy')}</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr
                  key={run.runId}
                  className="border-b border-[var(--color-border)] last:border-b-0"
                >
                  <td className="px-4 py-2">
                    <Link
                      to="/coorde/$runId"
                      params={{ runId: run.runId }}
                      className="text-[var(--color-text-primary)] underline-offset-2 hover:underline"
                    >
                      {new Date(run.createdAt).toLocaleString('ja-JP')}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-[var(--color-text-primary)]">
                    {t(`itemCatalog.season.${run.season}`)}
                  </td>
                  <td className="px-4 py-2 text-[var(--color-text-muted)]">
                    {t(`coordeHistory.status.${run.status}`)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-[var(--color-text-primary)]">
                    {run.generatedCount} / {run.setCount}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-[var(--color-text-primary)]">
                    {run.approvedCount}
                  </td>
                  <td
                    className={`px-4 py-2 text-right tabular-nums ${
                      needsJudgement(run)
                        ? 'text-[var(--color-warning)]'
                        : 'text-[var(--color-text-muted)]'
                    }`}
                  >
                    {run.unjudgedCount}
                  </td>
                  <td className="px-4 py-2 text-[var(--color-text-muted)]">{run.requestedBy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[12px] text-[var(--color-text-muted)]">{t('coordeHistory.note')}</p>
    </div>
  );
}
